// src/store/slices/assetsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getAssets,
  createAsset as createAssetAPI,
  updateAsset as updateAssetAPI,
  deleteAsset as deleteAssetAPI,
  Asset as BackendAsset,
  CreateAssetData,
  UpdateAssetData
} from 'api/assetsAPI';

// ---------------------- Types ----------------------

// Updated to match backend exactly
export type AssetStatus = 'available' | 'assigned' | 'maintenance' | 'retired';
export type AssetCondition = 'excellent' | 'good' | 'fair' | 'poor';

export interface AssetDataType {
  // Core identification
  id: number;
  name: string;
  asset_tag: string;
  serial_number: string;

  // Device details
  brand: string;
  model: string;
  description: string;

  // Status and condition
  status: AssetStatus;
  condition: AssetCondition;

  // Purchase information
  purchase_date: string;
  purchase_cost: number;
  purchase_invoice_number: string;
  vendor: string;
  warranty_expiry_date: string;

  // Location and assignment
  location: string;
  company_id: number;
  category_id: number;
  current_holder_id: number;

  // Additional display fields from backend
  category_name?: string;
  current_holder_name?: string;
  company_name?: string;

  // Timestamps
  created_at: string;
  updated_at: string;
}

// Payload interfaces
export interface CreateAssetPayload {
  assetData: Omit<
    AssetDataType,
    | 'id'
    | 'created_at'
    | 'updated_at'
    | 'category_name'
    | 'current_holder_name'
    | 'company_name'
  >;
  userCompanyId: number;
  categoryId: number;
  currentHolderId: number;
}

// ✅ FIXED: UpdateAssetPayload interface
export interface UpdateAssetPayload {
  assetId: number;
  assetData: Omit<
    AssetDataType,
    | 'id'
    | 'company_id'
    | 'category_id'
    | 'created_at'
    | 'updated_at'
    | 'category_name'
    | 'current_holder_name'
    | 'company_name'
  >;
}

interface AssetState {
  data: AssetDataType[];
  loading: boolean;
  error: string | null;
}

const initialState: AssetState = {
  data: [],
  loading: false,
  error: null
};

// ---------------------- Helper Functions ----------------------

// Convert backend Asset to frontend AssetDataType
const mapBackendToFrontend = (backendAsset: BackendAsset): AssetDataType => {
  return {
    id: backendAsset.id,
    name: backendAsset.name,
    asset_tag: backendAsset.asset_tag,
    serial_number: backendAsset.serial_number,
    brand: backendAsset.brand,
    model: backendAsset.model,
    description: backendAsset.description,
    status: backendAsset.status,
    condition: backendAsset.condition,
    purchase_date: backendAsset.purchase_date,
    purchase_cost: parseFloat(backendAsset.purchase_cost) || 0,
    purchase_invoice_number: backendAsset.purchase_invoice_number,
    vendor: backendAsset.vendor,
    warranty_expiry_date: backendAsset.warranty_expiry_date,
    location: backendAsset.location,
    company_id: backendAsset.company_id,
    category_id: backendAsset.category_id,
    current_holder_id: backendAsset.current_holder_id,
    created_at: backendAsset.created_at,
    updated_at: backendAsset.updated_at,
    category_name: backendAsset.category_name,
    current_holder_name: backendAsset.current_holder_name,
    company_name: backendAsset.company_name
  };
};

// Convert frontend AssetDataType to backend CreateAssetData
const mapFrontendToBackend = (
  frontendAsset: Omit<
    AssetDataType,
    | 'id'
    | 'created_at'
    | 'updated_at'
    | 'category_name'
    | 'current_holder_name'
    | 'company_name'
  >,
  userCompanyId: number,
  categoryId: number,
  currentHolderId: number
): CreateAssetData => {
  return {
    name: frontendAsset.name,
    asset_tag: frontendAsset.asset_tag,
    serial_number: frontendAsset.serial_number,
    brand: frontendAsset.brand,
    model: frontendAsset.model,
    description: frontendAsset.description,
    status: frontendAsset.status,
    condition: frontendAsset.condition,
    purchase_date: frontendAsset.purchase_date,
    purchase_cost: frontendAsset.purchase_cost,
    purchase_invoice_number: frontendAsset.purchase_invoice_number,
    vendor: frontendAsset.vendor,
    warranty_expiry_date: frontendAsset.warranty_expiry_date,
    location: frontendAsset.location,
    company_id: userCompanyId,
    category_id: categoryId,
    current_holder_id: currentHolderId
  };
};

// ✅ FIXED: Convert frontend AssetDataType to backend UpdateAssetData
const mapFrontendToUpdateBackend = (
  frontendAsset: Omit<
    AssetDataType,
    | 'id'
    | 'company_id'
    | 'category_id'
    | 'created_at'
    | 'updated_at'
    | 'category_name'
    | 'current_holder_name'
    | 'company_name'
  >
): UpdateAssetData => {
  return {
    name: frontendAsset.name,
    asset_tag: frontendAsset.asset_tag,
    serial_number: frontendAsset.serial_number,
    brand: frontendAsset.brand,
    model: frontendAsset.model,
    description: frontendAsset.description,
    status: frontendAsset.status,
    condition: frontendAsset.condition,
    purchase_date: frontendAsset.purchase_date,
    purchase_cost: frontendAsset.purchase_cost,
    purchase_invoice_number: frontendAsset.purchase_invoice_number,
    vendor: frontendAsset.vendor,
    warranty_expiry_date: frontendAsset.warranty_expiry_date,
    location: frontendAsset.location,
    current_holder_id: frontendAsset.current_holder_id
  };
};

// ---------------------- Async Thunks ----------------------

export const fetchAssets = createAsyncThunk<
  AssetDataType[],
  void,
  { rejectValue: string }
>('assets/fetchAssets', async (_, { rejectWithValue }) => {
  try {
    const backendAssets = await getAssets();
    return backendAssets.map(mapBackendToFrontend);
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch assets';
    return rejectWithValue(errorMessage);
  }
});

export const addAsset = createAsyncThunk<
  AssetDataType,
  CreateAssetPayload,
  { rejectValue: string }
>(
  'assets/addAsset',
  async (
    { assetData, userCompanyId, categoryId, currentHolderId },
    { rejectWithValue }
  ) => {
    try {
      const backendData = mapFrontendToBackend(
        assetData,
        userCompanyId,
        categoryId,
        currentHolderId
      );
      const createdAsset = await createAssetAPI(backendData);
      return mapBackendToFrontend(createdAsset);
    } catch (error: unknown) {
      // Extract detailed error message from backend
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { detail?: string; message?: string };
        };
        message?: string;
      };

      let errorMessage = 'Failed to create asset';

      if (axiosError.response?.data?.detail) {
        errorMessage = axiosError.response.data.detail;
      } else if (axiosError.response?.data?.message) {
        errorMessage = axiosError.response.data.message;
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ FIXED: editAsset thunk
export const editAsset = createAsyncThunk<
  AssetDataType,
  UpdateAssetPayload,
  { rejectValue: string }
>('assets/editAsset', async ({ assetId, assetData }, { rejectWithValue }) => {
  try {
    const updateData = mapFrontendToUpdateBackend(assetData);
    const backendAsset = await updateAssetAPI(assetId, updateData);
    return mapBackendToFrontend(backendAsset);
  } catch (error: unknown) {
    const axiosError = error as {
      response?: {
        status?: number;
        data?: {
          detail?: string;
          message?: string;
          error?: string;
        };
      };
      message?: string;
    };

    let errorMessage = 'Failed to update asset';

    if (axiosError.response?.data?.detail) {
      errorMessage = axiosError.response.data.detail;
    } else if (axiosError.response?.data?.message) {
      errorMessage = axiosError.response.data.message;
    } else if (axiosError.response?.data?.error) {
      errorMessage = axiosError.response.data.error;
    } else if (axiosError.message) {
      errorMessage = axiosError.message;
    }

    console.error('Update asset backend error:', axiosError.response?.data);
    return rejectWithValue(errorMessage);
  }
});

export const deleteAsset = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('assets/deleteAsset', async (assetId, { rejectWithValue }) => {
  try {
    await deleteAssetAPI(assetId);
    return assetId;
  } catch (error: unknown) {
    const axiosError = error as {
      response?: {
        data?: {
          detail?: string;
          message?: string;
          error?: string;
        };
      };
      message?: string;
    };

    let errorMessage = 'Failed to delete asset';

    // Check for backend error messages in order of preference
    if (axiosError.response?.data?.detail) {
      errorMessage = axiosError.response.data.detail;
    } else if (axiosError.response?.data?.message) {
      errorMessage = axiosError.response.data.message;
    } else if (axiosError.response?.data?.error) {
      errorMessage = axiosError.response.data.error;
    } else if (axiosError.message) {
      errorMessage = axiosError.message;
    }

    console.error('Delete asset backend error:', axiosError.response?.data);
    return rejectWithValue(errorMessage);
  }
});

// ---------------------- Slice ----------------------

const assetsSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      // Fetch
      .addCase(fetchAssets.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAssets.fulfilled,
        (state, action: PayloadAction<AssetDataType[]>) => {
          state.loading = false;
          state.data = action.payload;
        }
      )
      .addCase(fetchAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch assets';
      })

      // Add
      .addCase(addAsset.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        addAsset.fulfilled,
        (state, action: PayloadAction<AssetDataType>) => {
          state.loading = false;
          state.data.push(action.payload);
        }
      )
      .addCase(addAsset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create asset';
      })

      // Edit
      .addCase(editAsset.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        editAsset.fulfilled,
        (state, action: PayloadAction<AssetDataType>) => {
          state.loading = false;
          const index = state.data.findIndex(
            asset => asset.id === action.payload.id
          );
          if (index !== -1) {
            state.data[index] = action.payload;
          }
        }
      )
      .addCase(editAsset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update asset';
      })

      // Delete
      .addCase(deleteAsset.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteAsset.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.loading = false;
          state.data = state.data.filter(asset => asset.id !== action.payload);
        }
      )
      .addCase(deleteAsset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete asset';
      });
  }
});

export const { clearError } = assetsSlice.actions;
export default assetsSlice.reducer;
// Add these selectors to the end of assetsSlice.ts, before the export

// Selectors
export const selectAssets = (state: { assets: AssetState }) =>
  state.assets.data;
export const selectAssetsLoading = (state: { assets: AssetState }) =>
  state.assets.loading;
export const selectAssetsError = (state: { assets: AssetState }) =>
  state.assets.error;
